import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { withStyles } from '@material-ui/core/styles';
import { Add, Clear, Search } from '@material-ui/icons';
import Pagination from '@material-ui/lab/Pagination';
import axios from 'axios';
import moment from 'moment';
import { Box, IconButton, InputBase, List, ListItem, Paper, Typography } from '@material-ui/core';

import SkeletonTab from './SkeletonTab';
import UserInfoCard from './UserInfoCard';
import styles from './styles';
import useGlobalContext from '../../../common/hooks/useGlobalContext';
import messages from './messages';

const ADMIN = 'ADMIN';
const RIDER = 'RIDER';
const USER = 'USER';

const searchDelay = 2000;

const UserInfoTab = (props) => {
  const {
    classes,
    info,
    intl: { formatMessage },
    isCarsharing,
    handleChange,
    pageSize,
    setLoader,
    setRightSidePanelOpen,
    tabType,
    totalInvites,
    userPage,
  } = props;
  const [filteredUsers, setFilteredUsers] = useState(info);
  const [searchDelayId, setSearchDelayId] = useState();
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);

  const noUsersMessage = () => {
    switch (tabType) {
      case ADMIN:
        return formatMessage(messages.noAdminsFound);
      case RIDER:
        return formatMessage(messages.noRidersFound);
      case USER:
        return formatMessage(messages.noUsersFound);
      default:
        return formatMessage(messages.noResultsFound);
    }
  };
  const {
    state: { activeBusinessId, activeServiceId, loading },
    setLoading,
    setProfileFormPanel,
    setToastState,
    setError
  } = useGlobalContext();

  useEffect(() => {
    setFilteredUsers(info);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  const fetchUsers = async (search) => {
    let params = {
      business_entity_ids: activeBusinessId,
      ordering: '-id',
      search,
    };

    setLoading(true);
    switch (tabType) {
      case ADMIN:
        params.service_id = activeServiceId;
        await axios.get('/api/v2/staff', { params }).then(({ data }) => {
          setFilteredUsers(data);
          setSearching(false);
        });
        break;
      case RIDER:
        params.timestamp = moment().unix();
        params.all_customers = true;
        params.service_id = activeServiceId;
        await axios.get('/api/v2/customers', { params }).then(({ data }) => {
          setFilteredUsers(data);
          setSearching(false);
        });
        break;
      case USER:
        delete params.business_entity_ids;
        params.business_entity_id = activeBusinessId;
        await axios.get('/api/v3/user_invites', { params }).then(({ data: { results } }) => {
          setFilteredUsers(results);
          setSearching(false);
        });
        break;
      default:
        break;
    }
    setLoading(false);
  };

  const searchUsers = (e) => {
    const search = e && e.target ? e.target.value : '';
    setSearchValue(search);
    clearTimeout(searchDelayId);
    if (search) {
      setSearchDelayId(
        setTimeout(() => {
          setSearching(true);
          fetchUsers(search);
        }, searchDelay)
      );
    }
  };

  const clearUsers = () => {
    setSearchValue('');
    setSearching(false);
    setFilteredUsers(info);
  };

  const renderAddButtonText = (tabType) => {
    if (tabType === ADMIN) {
      return formatMessage(messages.addAdmin);
    } else if (tabType === USER) {
      return formatMessage(messages.addUser);
    }
    return formatMessage(messages.addRider);
  };


  const uploadCsv = (e) => {
    setLoader(true)
    const file = e.target.files[0]
    const data = new FormData()
    data.append('business_entity_id', activeBusinessId) 
    data.append('file', file)
    axios.post('/api/v3/user_invites/bulk_upload', data)
    .then((response) => {
      setLoader(false)
      setToastState({message: response.data.message, open: true, variant: 'success'})
    })
    .catch((error) => {
      setLoader(false)
      if(error.response.data?.message)
      setToastState({message: error.response.data.message, open: true, variant: 'error'})
      else
      setError(error)
    })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => clearUsers, []);

  const usersList = searchValue && !searching ? filteredUsers : info;
  const userContent =
    usersList.length > 0 ? (
      <List>
        {usersList.map((user) => (
          <UserInfoCard
            key={user.id}
            isCarsharing={isCarsharing}
            setRightSidePanelOpen={setRightSidePanelOpen}
            user={user}
          />
        ))}
      </List>
    ) : (
      <Box className={classes.centeredMessage}>
        <Typography>{noUsersMessage}</Typography>
      </Box>
    );


  const Label = props => <label htmlFor="upload-csv" {...props} />


  return (
    <Box className={classes.contentScroll}>
      <Paper className={classes.search} square>
        <IconButton disabled>
          <Search />
        </IconButton>
        <InputBase
          className={classes.searchBar}
          disabled={loading}
          onChange={searchUsers}
          placeholder={formatMessage(messages.search)}
          value={searchValue}
        />
        <IconButton disabled={loading}>
          <Clear onClick={clearUsers} />
        </IconButton>
      </Paper>
      <Box className={classes.flexAndSpaceBetween}>
        <ListItem
          button
          className={classes.listItem}
          onClick={() => {
            setProfileFormPanel(tabType.toLowerCase());
            if (tabType === USER) {
              setRightSidePanelOpen(true);
            }
          }}
        >
          <Add className={classes.addButton} />
          <div className={classes.block}>
            <Typography className={classes.addButtonText}>{renderAddButtonText(tabType)}</Typography>
            <Typography variant="body2">{formatMessage(messages.clickToOpenForm)}</Typography>
          </div>
        </ListItem>
        <ListItem
          button
          className={classes.listItem}
          component={Label}
          htmlFor={'upload-csv'}
        >
          <input
            accept=".csv"
            className={classes.input}
            hidden
            id="upload-csv"
            onChange={uploadCsv}
            type="file"
          />
          <Add className={classes.addButton} />
          <div className={classes.block}>
          <Typography className={classes.addButtonText}>{formatMessage(messages.uploadCsv)}</Typography>
          <Typography variant="body2">{formatMessage(messages.clickToUploadCsv)}</Typography>
          </div>
        </ListItem>
      </Box>
      {loading ? <SkeletonTab /> : userContent}
      <Pagination
        className={classes.pagination}
        count={Math.ceil(totalInvites / pageSize)}
        defaultPage={userPage}
        onChange={handleChange}
        size="large"
        shape="rounded"
        variant="outlined"
      />
    </Box>
  );
};

export default withStyles(styles)(injectIntl(UserInfoTab));
